import { PopUpMessageContext, useNavigationStore } from '@hb/react'
import {
  AnyObject,
  Field,
  FieldMap,
  FieldMapValue,
  isField,
  ListField,
  UploadProgress,
} from '@hb/shared'
import { FORM_ERROR, FormApi, ValidationErrors } from 'final-form'
import { useCallback, useContext } from 'react'
import { OnFormSubmit } from '../types'

export const useFormSubmit = <T extends AnyObject>(
  field: Field | FieldMap | ListField,
  onSubmit: OnFormSubmit<T>,
  setUploads: (updated: Record<string, UploadProgress> | null) => void,
  defaultCloseOnSuccess?: boolean,
  onClose?: () => void,
) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const onUploadProgress = useCallback(
    (updated: Record<string, UploadProgress>) => {
      setUploads({ ...updated })
    },
    [setUploads],
  )
  const submit = useCallback(
    async (
      updated: FieldMapValue,
      form: FormApi<T>,
      closeOnSuccess?: boolean,
    ): Promise<ValidationErrors> => {
      try {
        const updatedValue = isField(field) ? updated.value : updated
        const res = await onSubmit(
          updatedValue,
          onUploadProgress,
          form,
          closeOnSuccess ?? defaultCloseOnSuccess,
        )
        if (!res || res.success) {
          if (res?.success) processResponse({ success: res.success })
          useNavigationStore.getState().setUnsavedFormChange(null)
          if (closeOnSuccess && onClose) onClose()
          setUploads(null)
          return undefined
        }
        if (res.error) {
          processResponse({ error: res.error })
        }
        return res
      } catch (e: any) {
        console.error(e)
        setUploads(null)
        processResponse({ error: e?.message || 'An error occurred' })
        return { [FORM_ERROR]: e?.message || 'An error occurred' }
      }
    },
    [
      field,
      onUploadProgress,
      onSubmit,
      processResponse,
      setUploads,
      defaultCloseOnSuccess,
      onClose,
    ],
  )

  const onFormSubmit = useCallback(
    (updated: FieldMapValue, form: FormApi<T>) => submit(updated, form, defaultCloseOnSuccess),
    [submit, defaultCloseOnSuccess],
  )

  return { submit, onFormSubmit }
}
